import { Customer } from "gen/customer/models_pb"
import { BoolFilter } from "gen/search/service/service_pb"
import { useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import {
  Combobox,
  ComboboxProps,
} from "shared-web/components/base/input/Combobox"

import { useSearchCustomersV2 } from "features/api/search"

type CustomerComboboxProps = Omit<
  ComboboxProps<Customer>,
  "items" | "keyExtractor" | "valueExtractor" | "onChangeQuery"
> & {
  enableClear?: boolean
}

const CLEAR_CUSTOMER_SELECTION = "Clear selection"

export function CustomerCombobox({
  enableClear = false,
  onSelectItem,
  ...props
}: CustomerComboboxProps) {
  const { setQuery: setCustomerQuery, results: customerResults } =
    useSearchCustomersV2(
      { isActiveFilter: BoolFilter.TRUE },
      { ignoreSearchQuery: true }
    )

  const customersForFilter = useMemo(() => {
    const customers: Customer[] = []
    for (const customerResult of customerResults) {
      if (customerResult.customer) {
        customers.push(customerResult.customer)
      }
    }
    return customers
  }, [customerResults])

  const customerOptions = enableClear
    ? [
        Customer.create({ name: CLEAR_CUSTOMER_SELECTION }),
        ...customersForFilter,
      ]
    : customersForFilter

  const handleSelectItem = (customer: Customer | null) => {
    if (customer?.name === CLEAR_CUSTOMER_SELECTION) {
      onSelectItem(null)
    } else {
      onSelectItem(customer)
    }
  }

  return (
    <Combobox<Customer>
      items={customerOptions}
      onSelectItem={handleSelectItem}
      onChangeQuery={setCustomerQuery}
      keyExtractor={(customer) =>
        customer?.id ? UUID.fromPB(customer.id).toString() : ""
      }
      valueExtractor={(customer) => customer?.name ?? ""}
      {...props}
    />
  )
}
